import { apiClient } from '../utils/api-client';
// Shared so the server's error code survives the throw: the roster is refused
// with `dpa_required` until the gym signs, and the page routes on that.
import { throwForResponse } from './api-error';
import type {
  GymMember,
  GymMemberPage,
  GymMemberPayload,
  GymMemberResponse,
  GymMembersListResponse,
} from '../types/gym-member';


class GymMembersService {
  /**
   * One page of the roster. `search` runs a full-text query server-side, so
   * matching is the backend's business, not the client's.
   */
  async list(
    gymSlug: string,
    options: { search?: string; page?: number; perPage?: number } = {}
  ): Promise<GymMemberPage> {
    const params = new URLSearchParams();
    if (options.search) params.set('search', options.search);
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));

    const query = params.toString();
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/members${query ? `?${query}` : ''}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch members');
    }

    const data: GymMembersListResponse = await response.json();
    return {
      members: data.data.map((item) => item.attributes),
      page: data.meta?.page ?? 1,
      total: data.meta?.total ?? data.data.length,
      hasMore: data.meta?.has_more ?? false,
    };
  }

  async get(gymSlug: string, id: number): Promise<GymMember> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/members/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the member');
    }

    const data: GymMemberResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Creates a roster row. When the email matches a DivePeaks account the
   * backend attaches it on the spot, so the response may come back linked.
   */
  async create(gymSlug: string, payload: GymMemberPayload): Promise<GymMember> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/members`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create member');
    }

    const data: GymMemberResponse = await response.json();
    return data.data.attributes;
  }

  async update(gymSlug: string, id: number, payload: GymMemberPayload): Promise<GymMember> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/members/${id}`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update member');
    }

    const data: GymMemberResponse = await response.json();
    return data.data.attributes;
  }

  async remove(gymSlug: string, id: number): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/members/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete member');
    }
  }

  /**
   * Registers a climber whose QR was just scanned as a member, already linked.
   * The signed code is re-checked server-side, so it must be the one the
   * profile page verified — not something the client made up.
   */
  async registerScanned(gymSlug: string, userSlug: string, code: string): Promise<GymMember> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/members/register_scanned`, {
      user_slug: userSlug,
      code,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to register the climber');
    }

    const data: GymMemberResponse = await response.json();
    return data.data.attributes;
  }
}

export const gymMembersService = new GymMembersService();
